import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { getCdpAiTech } from "../../api";
import InfoTip from "../shared/InfoTip";
import PanelTakeaway from "../shared/PanelTakeaway";

function fmtTime(s) { if (!s) return "0s"; const m = Math.floor(s / 60); return m > 0 ? `${m}m ${s % 60}s` : `${s}s`; }
function fmtDate(ts) { if (!ts) return ""; return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }

export default function CdpAiTechTab({ clientId, rangeStart, rangeEnd, includeBots = false, isDarkMode, say }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId || !rangeStart || !rangeEnd) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const resp = await getCdpAiTech(clientId, rangeStart, rangeEnd, { includeBots });
        if (!cancelled) setData(resp?.data || null);
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId, rangeStart, rangeEnd, includeBots]);

  if (loading) return <div className="text-center py-12 text-gray-400">Loading AI & Technology data...</div>;
  if (!data) return <div className="text-center py-12 text-gray-400 text-sm">No data available</div>;

  // Defensive defaults (re-tier made this always-mount): every array field falls
  // back to [] and ad_blocker to {} so a missing sub-key never crashes .map/.find.
  const { ai_crawlers = [], humans_from_ai_answers = [], total_ai_humans = 0, ad_blocker = {}, languages = [], timezones = [], connections = [], device_gap = [], screen_resolutions = [], device_mix = [] } = data;
  const nonEnglish = languages.filter(l => l.browser_language && !l.browser_language.startsWith("en"));
  const desktopGap = device_gap.find(d => d.device_type === "desktop");
  const mobileGap = device_gap.find(d => d.device_type === "mobile");

  return (
    <div className="space-y-6">

      {/* ═══ 8A1: AI PLATFORMS INDEXING YOUR SITE (crawlers — bots, not humans) ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <PanelTakeaway panelKey="ai_crawlers" say={say} />
        <div className="flex items-center gap-2 mb-1">
          <span className="text-base">{"\ud83e\udd16"}</span>
          <h3 className="gi-panel-title text-gray-900 dark:text-white">AI Platforms Indexing Your Site</h3>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          AI crawlers that fetched your pages. Drives whether AI assistants can surface you in answers.
          <span className="block text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">These are <strong>bots</strong>, not visitors — the headline is crawl events, not people.</span>
        </p>
        <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg px-4 py-3 mb-4">
          <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">
            {ai_crawlers.length} AI platform{ai_crawlers.length !== 1 ? "s" : ""} crawled your site, generating {ai_crawlers.reduce((s, c) => s + (c.crawl_events || 0), 0).toLocaleString()} crawl event{ai_crawlers.reduce((s, c) => s + (c.crawl_events || 0), 0) !== 1 ? "s" : ""}.
          </span>
        </div>

        {ai_crawlers.length > 0 ? (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 dark:border-gray-800">
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Platform</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Crawl Events</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Days Active</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Pages</th>
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Last Crawl</th>
            </tr></thead>
            <tbody>{ai_crawlers.map((c, i) => (
              <tr key={c.ai_source} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""}`}>
                <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">{c.ai_source}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300">{c.crawl_events}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300">{c.days_active}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300">{c.pages_crawled}</td>
                <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">{fmtDate(c.last_crawl)}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : (
          <div className="text-center py-6 text-xs text-gray-400 dark:text-gray-500">No AI crawlers detected in this period.</div>
        )}
      </div>

      {/* ═══ 8A2: HUMANS FROM AI ANSWERS (real people referred by ChatGPT/Gemini/etc) ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <PanelTakeaway panelKey="ai_humans" say={say} />
        <div className="flex items-center gap-2 mb-1">
          <span className="text-base">{String.fromCodePoint(0x1F464)}</span>
          <h3 className="gi-panel-title text-gray-900 dark:text-white">Humans From AI Answers</h3>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          Real visitors who clicked through from an AI assistant's answer (referrer matches ChatGPT, Gemini, Claude, Perplexity, Copilot, or Google AI Overview). Excludes crawlers.
        </p>
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-4 py-3 mb-4">
          <span className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
            {total_ai_humans} human visitor{total_ai_humans !== 1 ? "s" : ""} arrived from an AI answer this period.
          </span>
        </div>

        {humans_from_ai_answers.length > 0 ? (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 dark:border-gray-800">
              <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">AI Source</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Visitors</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Sessions</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Book Clicks</th>
              <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400">Bookings</th>
            </tr></thead>
            <tbody>{humans_from_ai_answers.map((v, i) => (
              <tr key={v.source} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""}`}>
                <td className="px-3 py-2 text-xs font-medium text-gray-900 dark:text-white">{v.source}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300">{v.visitors}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300">{v.sessions}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300">{v.book_clicks}</td>
                <td className="px-3 py-2 text-center text-xs text-gray-700 dark:text-gray-300">{v.bookings}</td>
              </tr>
            ))}</tbody>
          </table>
        ) : (
          <div className="text-center py-6 text-xs text-gray-400 dark:text-gray-500">
            No humans referred from AI answers in this period yet. As AI assistants surface your property more, this number will grow.
          </div>
        )}
      </div>

      {/* ═══ 8B: TECHNOLOGY BREAKDOWN ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Ad Blocker — positive differentiator (Epic 8): these visitors are
            invisible to GA & Facebook Pixel but CampVantage tracks them all.
            Styled as a win (emerald), never a scary red number. */}
        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl border border-emerald-200 dark:border-emerald-800 p-5">
          <PanelTakeaway panelKey="ad_blocker" say={say} />
          <div className="gi-label text-emerald-700 dark:text-emerald-300 flex items-center gap-1">Ad-Block Visitors You Can See <InfoTip text="Share of visitors using ad blockers. Google Analytics and Facebook Pixel can't see these visitors — CampVantage tracks them all." /></div>
          <div className="gi-number text-emerald-700 dark:text-emerald-300 mt-1">{Math.min(ad_blocker.ad_blocker_pct || 0, 100)}%</div>
          <div className="text-xs text-emerald-700 dark:text-emerald-300 mt-1 font-medium">{ad_blocker.with_ad_blocker} of {ad_blocker.total_visitors} visitors use ad blockers — invisible to Google Analytics &amp; Facebook Pixel, fully tracked here.</div>
        </div>

        {/* Device Completion Gap — 2026-08-14 (Epic 5): show empty state when
            both completion rates are 0 (no booking completions tracked), instead
            of "Complete: 0%" which reads as a finding. */}
        {mobileGap && desktopGap && (mobileGap.completion_rate > 0 || desktopGap.completion_rate > 0) ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <PanelTakeaway panelKey="device_gap" say={say} />
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Device Completion Gap <InfoTip text="Comparison of booking completion rates between mobile and desktop visitors. Shows whether one device type converts better than the other." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                <div className="text-lg mb-1">{"\ud83d\udcf1"}</div>
                <div className="text-xs font-semibold text-gray-900 dark:text-white">Mobile</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{mobileGap.visitors} visitors</div>
                <div className="mt-2 text-xs">Book Click: <span className="font-medium">{Math.min(mobileGap.click_rate || 0, 100)}%</span></div>
                <div className="text-xs">Complete: <span className="font-medium">{Math.min(mobileGap.completion_rate || 0, 100)}%</span></div>
                {desktopGap.completion_rate - mobileGap.completion_rate > 10 && (
                  <div className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">{"\u26a0\ufe0f"} Gap: -{Math.round(desktopGap.completion_rate - mobileGap.completion_rate)}pts</div>
                )}
              </div>
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 text-center">
                <div className="text-lg mb-1">{"\ud83d\udcbb"}</div>
                <div className="text-xs font-semibold text-gray-900 dark:text-white">Desktop</div>
                <div className="text-xs text-gray-600 dark:text-gray-400">{desktopGap.visitors} visitors</div>
                <div className="mt-2 text-xs">Book Click: <span className="font-medium">{Math.min(desktopGap.click_rate || 0, 100)}%</span></div>
                <div className="text-xs">Complete: <span className="font-medium">{Math.min(desktopGap.completion_rate || 0, 100)}%</span></div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <PanelTakeaway panelKey="device_gap" say={say} />
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Device Completion Gap <InfoTip text="Comparison of booking completion rates between mobile and desktop visitors." /></div>
            <div className="text-center py-6 text-sm text-gray-400 dark:text-gray-500">
              No booking completions tracked for this period.
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Browser Language */}
        {languages.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Browser Language</div>
            {nonEnglish.length > 0 && (
              <div className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                {nonEnglish.reduce((s, l) => s + l.pct, 0).toFixed(1)}% of visitors browse in non-English languages.
              </div>
            )}
            <ResponsiveContainer width="100%" height={languages.length * 28 + 10}>
              <BarChart data={languages} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
                <XAxis type="number" tick={{ fontSize: 10 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
                <YAxis dataKey="browser_language" type="category" width={60} tick={{ fontSize: 10 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
                <Tooltip contentStyle={{ backgroundColor: isDarkMode ? "#1F2937" : "#fff", border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`, borderRadius: 8, fontSize: 11 }} />
                <Bar dataKey="visitors" fill="#6366F1" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Timezone Distribution */}
        {timezones.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Timezone Distribution</div>
            <div className="space-y-1.5">
              {timezones.map((t, i) => (
                <div key={t.timezone} className="flex items-center justify-between text-xs">
                  <span className={`text-gray-700 dark:text-gray-300 ${i === 0 ? "font-medium" : ""}`}>{t.timezone}</span>
                  <span className="text-gray-500 dark:text-gray-400">{t.visitors}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Connection Type */}
        {connections.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Connection Type</div>
            <div className="space-y-1.5">
              {connections.map(c => (
                <div key={c.connection_type} className="flex items-center justify-between text-xs">
                  <span className="text-gray-700 dark:text-gray-300">{c.connection_type}</span>
                  <span className="text-gray-500 dark:text-gray-400">{c.visitors} ({c.pct}%)</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Screen Resolution */}
        {screen_resolutions.length > 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Screen Resolution</div>
            <div className="space-y-1.5">
              {screen_resolutions.map(s => (
                <div key={s.screen_resolution} className="flex items-center justify-between text-xs">
                  <span className="text-gray-700 dark:text-gray-300 font-mono">{s.screen_resolution}</span>
                  <span className="text-gray-500 dark:text-gray-400">{s.visitors}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Device & Browser Mix */}
      {device_mix.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="gi-panel-title text-gray-900 dark:text-white">Device & Browser Mix</h3>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-14">Device</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Type</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Browser</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">OS</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Visitors</th>
              <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Book Rate</th>
            </tr></thead>
            <tbody>{device_mix.map((d, i) => (
              <tr key={i} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""}`}>
                <td className="px-4 py-2 text-center">{d.device_type === "mobile" ? "\ud83d\udcf1" : d.device_type === "tablet" ? "\ud83d\udcdf" : "\ud83d\udcbb"}</td>
                {/* 2026-08-14 (Epic 6) — add text label so two rows with the
                    same browser/OS but different devices are distinguishable. */}
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400 capitalize">{d.device_type || "Unknown"}</td>
                <td className="px-4 py-2 text-xs text-gray-700 dark:text-gray-300">{d.browser || "Unknown"}</td>
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">{d.os || "Unknown"}</td>
                <td className="px-4 py-2 text-center text-xs text-gray-700 dark:text-gray-300">{d.visitors}</td>
                <td className="px-4 py-2 text-right text-xs text-gray-600 dark:text-gray-400">{d.book_rate}%</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
